<template>
  <div class="line-chart w-100">
    <s-loader
      v-show="showLoader"
      class="line-chart__loader"
      :class="{ 'line-chart__loader--visible': showLoader }"
      size="lg"
    />
    <div class="line-chart__chart-wrapper">
      <e-chart
        class="e-chart"
        autoresize
        ref="echarts"
        outlined
        :class="{ 'border-0': !chartData.length }"
        @legendselectchanged="changeLegend"
        @restore="onRestore"
        @rendered="onRendered"
        @magictypechanged="changeChartType"
      />
      <img
        class="e-chart__image img-fluid w-100"
        alt="chart-image"
        :src="chartImg"
      >
      <s-empty-list
        class="line-chart__empty-list"
        v-show="showEmptyList"
      />
    </div>
    <div class="display-bookmarks" v-if="oscillographyChartType
    === oscillographyChartTypes.OSCILLOGRAPHY">
      <b-checkbox
        class="chart-legends__toggle-all"
        @change="checkChangeBookmarks"
      />
      <span>
          {{ $t('general.display-bookmarks') }}
      </span>
    </div>
    <div class="line-chart__legend">
      <oscillography-chart-legends
        v-if="chartData.length && !loading"
        :start-collapsed="startCollapsed"
        :items="legend"
        :maneuver-data="maneuverData"
        :chart-id="chartId"
        :hidden-legends="hiddenLegends"
        :items-unique-property="itemsUniqueProperty"
        :colors-list="colorsList"
        :oscillography-chart-type="oscillographyChartType"
        :bookmarks="bookmarks"
        :hide-maneuvers-section="hideManeuversSection"
        @toggle-legend="toggleLegendProxy"
        @update-hidden-legends="handleUpdateHiddenLegends"
        @select-all-legends="selectAllLegendsProxy"
        @unselect-all-legends="unselectAllLegendsProxy"
        @toggle-maneuver="toggleManeuver"
        @toggle-all-maneuvers="toggleAllManeuvers"
      />
    </div>
  </div>
</template>
<script>
import { mapGetters } from 'vuex';
import events from '@/domain/events/charts';
import chartConfiguration from '@/domain/services/charts/linesBuilderOscillography';
import { formatNumber } from '@/services/translate';
import globalEventBus from '@/services/globalEventBus';
import cloneDeep from 'lodash/cloneDeep';
import OscillographyChartLegends from '@/components/charts/oscillography/OscillographyChartLegends';
import themeService from '@/services/themeService';
import groupBy from 'lodash/groupBy';
import chartTypes from '@/domain/enums/chartTypes';
import oscillographyChartMixin from '@/components/mixins/oscillographyChartMixin';
import exponentialFilter,
{
  checkNumberIsValidForScientificNotation,
}
from '@/filters/exponentialFilter';
import oscillographyChartTypes from '@/domain/enums/oscillographyChartTypes';

const enabledColor = themeService.getFontColor();
const disabledColor = themeService.getStyleVar('font-color-disabled');

export default {
  props: {
    chartToolbox: {
      type: [Object, Boolean],
      default: true,
    },
    xDataManager: {
      type: [String, Function],
      required: false,
    },
    title: {
      type: Object,
      required: false,
    },
    firstColumnI18nTitle: {
      type: String,
      default: 'fields.general.date-time',
    },
    loading: {
      type: Boolean,
      default: false,
    },
    chartId: {
      type: String,
      required: true,
    },
    defaultShowTooltip: {
      type: Boolean,
      default: true,
    },
    defaultShowGrid: {
      type: Boolean,
      default: true,
    },
    defaultSmooth: {
      type: Boolean,
      default: true,
    },
    removeOutlier: {
      type: Boolean,
      default: false,
    },
    enableMultiAxis: {
      type: Boolean,
      default: true,
    },
    chartType: {
      type: String,
      default: chartTypes.LINE,
      validator: value => Object.values(chartTypes).includes(value),
    },
    oscillographyChartType: {
      type: String,
      default: oscillographyChartTypes.OSCILLOGRAPHY,
    },
    hideManeuversSection: {
      type: Boolean,
      default: false,
    },
    colorsList: {
      type: Array,
    },
    startCollapsed: {
      type: Boolean,
      default: false,
    },
    timeZone: {
      type: String || null,
      default: null,
    },
    startIndexFromOne: {
      type: Boolean,
      default: false,
    },
    isOscyllography: {
      type: Boolean,
      default: false,
    },
    maneuverData: {
      type: Array,
      required: false,
    },
  },
  components: {
    OscillographyChartLegends,
  },
  mixins: [oscillographyChartMixin],
  data: vm => ({
    chartsRefs: ['echarts'],
    showTooltip: vm.defaultShowTooltip,
    showGrid: vm.defaultShowGrid,
    smoothed: vm.defaultSmooth,
    localLoading: false,
    highlightedAxis: null,
    chartImg: null,
    tooltipListener: null,
    selectedChartType: null,
    bookmarks: true,
    oscillographyChartTypes,
  }),
  beforeCreate() {
    Object.defineProperty(this, 'chartOptions', {
      configurable: true,
      writable: true,
      value: {},
    });
  },
  computed: {
    ...mapGetters([
      'isPrinting',
    ]),
    showLoader() {
      return this.loading || this.localLoading;
    },
    showEmptyList() {
      return !this.showLoader && !this.chartData.length;
    },
    turnOffBoundaryGap() {
      return this.oscillographyChartType === oscillographyChartTypes.DIAGNOSTICS;
    },
  },
  mounted() {
    this.setChartOptions();
    this.initListeners();
  },
  beforeDestroy() {
    this.removeListeners();
  },
  methods: {
    initListeners() {
      globalEventBus.on(events.toggleTooltip, this.toggleTooltip, this);
      globalEventBus.on(events.toggleGrid, this.toggleGrid, this);
      globalEventBus.on(events.toggleOutlier, this.toggleOutlier, this);
      globalEventBus.on(events.toggleSmooth, this.toggleSmooth, this);
      globalEventBus.on(events.toggleMultiAxis, this.toggleMultiAxis, this);
      this.$refs.echarts
        .delegateMethod('on', 'click', 'series.line', this.handleHighlightAxis);
    },
    removeListeners() {
      globalEventBus.off(events.toggleTooltip, this.toggleTooltip, this);
      globalEventBus.off(events.toggleGrid, this.toggleGrid, this);
      globalEventBus.off(events.toggleOutlier, this.toggleOutlier, this);
      globalEventBus.off(events.toggleSmooth, this.toggleSmooth, this);
      globalEventBus.off(events.toggleMultiAxis, this.toggleMultiAxis, this);
      this.$refs.echarts
        .delegateMethod('off', 'click', 'series.line', this.handleHighlightAxis);
    },
    resetAllYAxisStyle() {
      this.chartOptions.yAxis = this.chartOptions.yAxis
        .map(() => this.getYAxisStyleConfiguration(themeService.getFontColor()));
    },
    resetHighlightedAxis() {
      this.resetAllYAxisStyle();
      this.highlightedAxis = null;
      this.$refs.echarts.delegateMethod('setOption', this.chartOptions);
      this.handleAxisColorByLegends();
    },
    handleUpdateHiddenLegends(newList) {
      this.hiddenLegends = newList;
    },
    getYAxisStyleConfiguration(color) {
      return {
        axisLabel: {
          color,
        },
        axisLine: {
          lineStyle: {
            color,
          },
        },
      };
    },
    handleHighlightAxis(item) {
      if (!this.enableMultiAxis || !Array.isArray(this.chartOptions.yAxis)) {
        this.highlightedAxis = null;
        return;
      }
      const { axis } = this.chartData[item.componentIndex];
      if (axis === this.highlightedAxis) {
        this.resetHighlightedAxis();
        return;
      }
      this.highlightAxis(axis);
    },
    highlightAxis(axis) {
      this.highlightedAxis = axis;
      this.chartOptions.yAxis = this.chartOptions.yAxis.map((yAxis, index) => {
        if (index === axis) {
          return this.getYAxisStyleConfiguration(enabledColor);
        }
        return this.getYAxisStyleConfiguration(disabledColor);
      });
      this.$refs.echarts.delegateMethod('setOption', this.chartOptions);
    },
    hideLoading() {
      this.localLoading = false;
    },
    startLoading() {
      this.localLoading = true;
    },
    toggleOutlier() {
      this.$emit('update:remove-outlier', !this.removeOutlier);
      this.$emit('toggle-outlier');
    },
    toggleSmooth(id) {
      if (id !== this.chartId) return;
      this.startLoading();
      this.smoothed = !this.smoothed;
      this.setChartOptions();
    },
    toggleMultiAxis() {
      this.$emit('update:enable-multi-axis', !this.enableMultiAxis);
      this.$emit('toggle-multi-axis');
    },
    toggleGrid(id) {
      if (id !== this.chartId) return;
      this.startLoading();
      this.showGrid = !this.showGrid;
      this.setChartOptions();
    },
    changeChartType(chartTypeConfiguration) {
      this.selectedChartType = chartTypeConfiguration.currentType;
      this.setChartOptions();
    },
    toggleTooltip(id) {
      if (id !== this.chartId) return;
      this.startLoading();
      this.showTooltip = !this.showTooltip;
      this.setChartOptions();
    },
    setChartOptions() {
      if (!this.chartData.length) {
        this.$refs.echarts.delegateMethod('clear');
        return;
      }
      const toolbox = this.isPrinting ? null : this.chartToolbox;
      const chartType = this.selectedChartType || this.chartType;
      const addBoundaryGap = this.turnOffBoundaryGap && chartType === chartTypes.BAR;
      this.chartOptions = chartConfiguration.generate(this.bookmarks, this.chartData, {
        legendAmount: this.legend.length,
        boundaryGap: addBoundaryGap,
        chartId: this.chartId,
        title: this.title,
        firstColumnTitle: this.firstColumnI18nTitle,
        toolbox,
        tooltipFormatter: this.tooltipFormatter,
        showTooltip: this.showTooltip,
        removeOutlier: this.removeOutlier,
        smoothed: this.smoothed,
        enableMultiAxis: this.enableMultiAxis,
        showGrid: this.showGrid,
        xDataManager: this.xDataManager,
        isPrinting: this.isPrinting,
        chartType,
        colorsList: this.colorsList,
        timeZone: this.timeZone,
        startIndexFromOne: this.startIndexFromOne,
      });
      this.$refs.echarts.delegateMethod(
        'setOption',
        this.chartOptions,
        { replaceMerge: ['tooltip', 'yAxis', 'series', 'toolbox'] },
      );
      this.highlightedAxis = null;
      this.$nextTick(() => {
        this.hideLoading();
      });
    },
    getEmptyAxis() {
      const groupedLegendsByAxis = groupBy(this.legend, 'axis');
      const groupedLegendsValues = Object.values(groupedLegendsByAxis);
      const axisValuesFilteredOfHiddenLegends = groupedLegendsValues.map(
        group => group.filter(
          legend => !this.hiddenLegends.includes(legend.name),
        ),
      );
      const emptyAxis = [];
      axisValuesFilteredOfHiddenLegends.forEach((group, axis) => {
        if (!group.length) {
          emptyAxis.push(axis);
        }
      });
      return emptyAxis;
    },
    async handleAxisColorByLegends() {
      await this.$nextTick();
      const emptyAxis = this.getEmptyAxis();
      if (!Array.isArray(this.chartOptions.yAxis)) {
        this.chartOptions.yAxis = emptyAxis.includes(0)
          ? this.getYAxisStyleConfiguration(disabledColor)
          : this.getYAxisStyleConfiguration(enabledColor);
      }
      if (Array.isArray(this.chartOptions.yAxis)) {
        this.chartOptions.yAxis = this.chartOptions.yAxis.map((yAxis, index) => {
          if (emptyAxis.includes(index)) return this.getYAxisStyleConfiguration(disabledColor);
          return this.getYAxisStyleConfiguration(enabledColor);
        });
      }
      this.highlightedAxis = null;
      this.$refs.echarts.delegateMethod('setOption', { yAxis: this.chartOptions.yAxis });
    },
    toggleLegendProxy(item) {
      this.toggleLegend(item);
      this.handleAxisColorByLegends();
    },
    selectAllLegendsProxy() {
      this.selectAllLegends();
      this.handleAxisColorByLegends();
    },
    unselectAllLegendsProxy() {
      this.unselectAllLegends();
      this.handleAxisColorByLegends();
    },
    mountTooltipValue(params) {
      const itemInfo = cloneDeep(params);
      const chartData = this.chartData[itemInfo.seriesIndex];
      const unit = chartData.unit === 'string' ? chartData.unit : '';
      const INDEX_CORRECTION_TO_NUMBER = 1;
      const fractionDigits = itemInfo.value[1].length;

      itemInfo.value[1] = checkNumberIsValidForScientificNotation(
        itemInfo.value[1],
      )
        ? exponentialFilter(itemInfo.value[1], 2)
        : formatNumber(itemInfo.value[1], {
            minimumFractionDigits: 1,
            maximumFractionDigits: fractionDigits,
          });

      let maneuverName = '';
      if (this.oscillographyChartType === oscillographyChartTypes.DIAGNOSTICS) {
        maneuverName = this.maneuverData.find(m => {
          const maneuverDate = new Date(m.dateTimeOscillography);
          const [datePart, timePartRaw] = itemInfo.value[0].split(', ');

          const [p1, p2, p3] = datePart.split('/');
          const timePart = timePartRaw.trim();

          let day;
          let month;
          let year;
          // eslint-disable-next-line radix
          if (parseInt(p1) > 12) {
            day = p1;
            month = p2;
            year = p3;
          } else {
            month = p1;
            day = p2;
            year = p3;
          }

          let hours;
          let minutes;
          let seconds;
          let ampm = null;

          if (timePart.includes('AM') || timePart.includes('PM')) {
            const [timeOnly, meridian] = timePart.split(' ');
            ampm = meridian.toUpperCase();
            [hours, minutes, seconds] = timeOnly
              .split(':')
              .map(v => parseInt(v, 10));

            if (ampm === 'PM' && hours < 12) hours += 12;
            if (ampm === 'AM' && hours === 12) hours = 0;
          } else {
            [hours, minutes, seconds] = timePart
              .split(':')
              .map(v => parseInt(v, 10));
          }

          const itemDate = new Date(
            // eslint-disable-next-line radix
            parseInt(year),
            // eslint-disable-next-line radix
            parseInt(month) - 1,
            // eslint-disable-next-line radix
            parseInt(day),
            hours,
            minutes,
            seconds,
          );

          return Math.abs(maneuverDate.getTime() - itemDate.getTime()) < 1000;
        })?.name;

        const originalSeriesName = itemInfo.seriesName;
        const maneuverRegex = /(Manobra|Maniobra|Maneuver|Manöver|Manovra):?\s*\d+/i;
        const updatedSeriesName = originalSeriesName.replace(
          maneuverRegex,
          `${maneuverName || 'N/A'}`,
        );

        return `
          ${itemInfo.marker} ${updatedSeriesName}<br/>
          X: ${itemInfo.value[0]}<br/>
          Y: ${itemInfo.value[1]} ${unit}<br/>
          ${this.$t('e-charts.y-axis-index')}: ${
            chartData.axis
              ? chartData.axis + INDEX_CORRECTION_TO_NUMBER
              : INDEX_CORRECTION_TO_NUMBER
          }
        `;
      }
      return `
        ${itemInfo.marker} ${itemInfo.seriesName}<br/>
        X: ${itemInfo.value[0]}<br/>
        Y: ${itemInfo.value[1]} ${unit}<br/>
        ${this.$t('e-charts.y-axis-index')}: ${
          chartData.axis
            ? chartData.axis + INDEX_CORRECTION_TO_NUMBER
            : INDEX_CORRECTION_TO_NUMBER
        }
      `;
    },
    tooltipFormatter(params, ticket, callback) {
      if (!this.showTooltip) return null;

      if (this.tooltipListener) {
        clearTimeout(this.tooltipListener);
      }

      const tooltipValue = this.mountTooltipValue(params, ticket, callback);
      this.tooltipListener = setTimeout(() => {
        callback(ticket, tooltipValue);
      }, 500);
      return this.$t('general.loading');
    },
    restoreLegends() {
      this.hiddenLegends = [];
      this.setChartOptions();
    },
    onRestore() {
      this.selectedChartType = this.chartType;
      this.highlightedAxis = null;
      this.restoreLegends();
    },
    onRendered() {
      if (!this.isPrinting) return;

      this.$nextTick(() => {
        this.chartImg = this.$refs.echarts.getDataURL();
      });
    },
    checkChangeBookmarks(event) {
      if (event) {
        this.bookmarks = false;
        this.setChartOptions();
      } else {
        this.bookmarks = true;
        this.setChartOptions();
      }
    },
    toggleManeuver(maneuverKey) {
      this.$emit('toggle-maneuver', maneuverKey);
    },
    toggleAllManeuvers(event) {
      this.$emit('toggle-all-maneuvers', event);
    },
  },
  watch: {
    chartData() {
      this.setChartOptions();
    },
    isPrinting(isPrinting) {
      if (!isPrinting) {
        this.chartImg = null;
      }
      const toolbox = null;
      this.setChartOptions();
      this.$refs.echarts.delegateMethod('setOption', { toolbox });
    },
  },
};
</script>
<style lang="sass" scoped>
@import "src/styles/defaults/variables"

.line-chart
  position: relative
  min-height: 100px
  outline: none
  padding-bottom: 30px
  &__loader
    position: absolute
    min-height: 100px
    height: 100%
    width: 100%
    background-color: var(--color-modal-background)
    z-index: 1

  &__empty-list
    position: absolute
    top: 50%
    left: 50%
    transform: translate(-50%, -50%)

  &__chart-wrapper
    position: relative
    width: 100%
    overflow-x: auto
    page-break-inside: avoid


.e-chart
  display: block
  margin-bottom: 15px
  min-width: 100%
  min-height: 100px
  overflow: hidden
  outline: none
  border: 1px solid $color-border-gray
  border-radius: 3px


  &__image
    display: none

  ::v-deep textarea
    white-space: pre

.display-bookmarks
  display: flex
  flex-direction: row
  padding: 10px 0 30px 0

@media print
  .e-chart
    display: none

    &__image
      display: block
</style>
